import {useAdviseSession} from 'hooks/adviseSessions';
import {entry} from 'providers/core/adviseSessions';
import {useCallback, useEffect, useRef} from 'react';
import {ApiError} from 'services/Api';
import {seenAllMessages} from 'utils/chat';

export let focusedAdviseSessionId = null;

export default function useChatEntry(id) {
  const {data: adviseSession} = useAdviseSession(id);
  const adviseSessionRef = useRef(adviseSession);
  const retryTimerRef = useRef();

  useEffect(() => {
    adviseSessionRef.current = adviseSession;
  }, [adviseSession]);

  const onEntered = useCallback(async () => {
    focusedAdviseSessionId = id;

    seenAllMessages(id);

    try {
      if (
        !adviseSessionRef.current ||
        !['CANCELED', 'REJECTED', 'CLOSED'].includes(
          adviseSessionRef.current.status,
        )
      ) {
        await entry(id, true);
      }
    } catch (e) {
      const retry =
        e instanceof ApiError && (e.code === 'TRY_LATER' || !e.data);
      if (retry) {
        retryTimerRef.current = setTimeout(onEntered, 5000);
      } else {
        throw e;
      }
    }
  }, [id]);

  const onExited = useCallback(async () => {
    if (focusedAdviseSessionId === id) {
      focusedAdviseSessionId = null;
    }

    if (retryTimerRef.current !== undefined) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = undefined;
    }

    if (
      !['CANCELED', 'REJECTED', 'CLOSED'].includes(
        adviseSessionRef.current?.status,
      )
    ) {
      try {
        await entry(id, false);
      } catch (e) {
        if (e.code !== 'TRY_LATER') {
          throw e;
        }
      }
    }
  }, [id]);

  useEffect(() => {
    onEntered();

    return () => onExited();
  }, [onEntered, onExited]);
}
